// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconQR } from 'components/atoms/icons/IconQR';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getApiPath, normalizeNumber, objectFetch } from 'util/commons';
import styles from './index.module.scss';
import CollectRouteBookingQRScanner from 'components/molecules/CollectRouteBookingQRScanner';
import IconListOutline from 'components/atoms/icons/IconListOutline';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import {
  getCollectRouteQRInfo,
  getRoutesCollectedTotalWeight,
  submitCollectRouteQR,
} from 'apis/collect_route';
import { QRCodeItemType, GroupedQRCodeType } from 'types/routeManagementTypes';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import { Path } from 'routes';
import { ConfirmSelectEmissionItemsModal } from '../ConfirmSelectEmissionItemsModal';
import { toast } from 'react-toastify';
import Portal from 'components/atoms/Portal';
import { ReportAbnormalityModal } from '../ReportAbnormalityModal';
import { useSelector } from 'react-redux';
import { selectCollectingRouteSites } from 'redux/slices/collectingRoute';
import { useCheckRouteCollectProcess } from 'util/hooks/useCheckRouteCollectProcess';
import CollectEmissionsSelectTruckModal from '../CollectEmissionsSelectTruckModal';
import { selectUser } from 'redux/slices/userSlice';
import { RoleType } from 'util/Enums';
import { CollectingRouteSession } from 'util/collectingRoute';

const CollectRouteQRScanning = ({
  routeId,
  siteId,
  setHasCollectedSuccess,
}: {
  routeId: string;
  siteId: string;
  setHasCollectedSuccess: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useSelector(selectUser);
  const collectingRouteSites = useSelector(selectCollectingRouteSites);
  const { state } = useLocation();

  const [latestInfoQRCode, setLatestInfoQRCode] =
    useState<QRCodeItemType | null>(null);
  const [groupedQRCode, setGroupedQRCode] = useState<GroupedQRCodeType[]>([]);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [reportingTargetIds, setReportingTargetIds] = useState<string[] | null>(
    null
  );
  const [isShowTruckModal, setIsShowTruckModal] = useState<boolean>(false);
  const [selectedTruckId, setSelectedTruckId] = useState<string>('');

  const hasEdiEnabledCheckedItem = groupedQRCode.some(
    (group) =>
      group.isEdiEnabled && group.data.some((item) => checkedItems.has(item.id))
  );
  useCheckRouteCollectProcess();

  const { areaId, areaName } = useMemo(() => {
    const currentSite = collectingRouteSites?.sites.find(
      (site) => site.id === siteId
    );

    return {
      areaId: currentSite?.area.id || '',
      areaName: currentSite?.area.name || '',
    };
  }, [collectingRouteSites, siteId]);

  const handleAddQRCodeToGroup = (data: QRCodeItemType) => {
    const wasteUnitId = data.wasteUnit.id.toString();
    const newItem = {
      deliveryId: data.deliveryId,
      emissionStatus: data.emissionStatus,
      id: data.id,
      weight: data.weight,
    };

    // Add new item to checked items by default
    setCheckedItems((prev) => new Set(prev).add(data.id));

    setGroupedQRCode((prevGrouped) => {
      const groupIndex = prevGrouped.findIndex((g) => g.id === wasteUnitId);

      // Create new group if doesn't exist
      if (groupIndex === -1) {
        return [
          ...prevGrouped,
          {
            id: wasteUnitId,
            name: data.wasteUnit.name,
            isEdiEnabled: data.wasteUnit.isEdiEnabled,
            data: [newItem],
          },
        ];
      }

      // Check duplicate and return unchanged if exists
      if (prevGrouped[groupIndex].data.some((item) => item.id === data.id)) {
        return prevGrouped;
      }

      // Add to existing group
      const updatedGroups = [...prevGrouped];
      updatedGroups[groupIndex] = {
        ...updatedGroups[groupIndex],
        data: [...updatedGroups[groupIndex].data, newItem],
      };
      return updatedGroups;
    });
  };
  const callbackGetQrCodeId = async (qrCode: string) => {
    if (!qrCode) {
      return;
    }

    getCollectRouteQRInfo({
      siteId: siteId,
      qrCode: qrCode,
      successCallback: (data) => {
        if (data && data.id) {
          toast.success(t('messages.M_136'));
          setLatestInfoQRCode(data);
          handleAddQRCodeToGroup(data);
        }
      },
    });
  };

  const handleToggleItem = (itemId: string) => {
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleCollectEmissionItems = (isCompleted: boolean) => {
    const emissionIds = Array.from(checkedItems);

    submitCollectRouteQR({
      routeId:
        routeId != null && routeId !== 'all' && routeId !== 'none'
          ? Number(routeId)
          : undefined,
      areaId: siteId,
      emissionIds,
      ...(!!selectedTruckId && { truckId: Number(selectedTruckId) }),
      successCallback: () => {
        toast.success(t('messages.M_087'));
        setIsShowConfirmModal(false);
        setIsShowTruckModal(false);
        setSelectedTruckId('');
        setGroupedQRCode((prevGroups) => {
          return prevGroups
            .map((group) => ({
              ...group,
              data: group.data.filter((item) => !checkedItems.has(item.id)),
            }))
            .filter((group) => group.data.length > 0);
        });
        setCheckedItems(new Set());
        CollectingRouteSession.addProcessedSite({
          routeId,
          siteId,
        });
        getRoutesCollectedTotalWeight();

        if (isCompleted) {
          history.push(
            `${getApiPath(Path.routeSiteSelection, {
              routeId: objectFetch(state, 'routeId') || routeId,
            })}${window.location.search}`,
            {
              refreshSites: true,
            }
          );
        } else {
          setHasCollectedSuccess(true);
        }
      },
    });
  };

  const handleTruckSubmit = (truckId: string) => {
    setSelectedTruckId(truckId);
    setIsShowConfirmModal(true);
  };

  const handleCloseTruckModal = () => {
    setSelectedTruckId('');
    setIsShowTruckModal(false);
  };

  const calculateTotalWeight = (data: GroupedQRCodeType['data']) => {
    const result = data.reduce((total, item) => {
      if (!checkedItems.has(item.id)) return total;
      const weight = parseFloat(item.weight) || 0;
      return total + weight;
    }, 0);
    return normalizeNumber({ value: result.toString() });
  };

  const calculateCheckedItemsTotalWeight = useMemo(() => {
    let total = 0;
    groupedQRCode.forEach((group) => {
      group.data.forEach((item) => {
        if (checkedItems.has(item.id)) {
          const weight = parseFloat(item.weight) || 0;
          total += weight;
        }
      });
    });
    return total;
  }, [groupedQRCode, checkedItems]);

  const getSelectedItems = useMemo(() => {
    const result: Record<
      string,
      {
        name: string;
        weight: string;
      }
    > = {};

    groupedQRCode.forEach((group) => {
      const checkedItemsInGroup = group.data.filter((item) =>
        checkedItems.has(item.id)
      );

      if (checkedItemsInGroup.length > 0) {
        const totalWeight = checkedItemsInGroup.reduce((total, item) => {
          return total + (parseFloat(item.weight) || 0);
        }, 0);

        result[group.id] = {
          name: group.name,
          weight: totalWeight.toString(),
        };
      }
    });

    return result;
  }, [groupedQRCode, checkedItems]);

  const totalItemCount = useMemo(() => {
    return groupedQRCode.reduce((total, group) => total + group.data.length, 0);
  }, [groupedQRCode]);

  return (
    <div className={styles.collectRouteQRScanning}>
      <div className={styles.header}>
        <div className={styles.iconQR}>
          <IconQR />
        </div>
        <div>
          <p className={styles.title}>{t('qr_scan.title')}</p>
        </div>
      </div>
      <div className={styles.scannerContainter}>
        <div className={styles.scannerLeft}>
          <CollectRouteBookingQRScanner
            successCallback={callbackGetQrCodeId}
            // Pause scanning when reporting modal is open to prevent multiple camera accessed that may cause performance issue
            paused={reportingTargetIds !== null}
          />

          {latestInfoQRCode != null && (
            <div className={styles.scannerResult}>
              <p className={styles.scanSuccessText}>{t('qr_scan.success')}</p>

              <p className={styles.scanItemNameLabel}>
                {t('qr_scan.item_name')}
              </p>
              <p className={styles.scanItemName}>
                {latestInfoQRCode.wasteUnit.name}
              </p>
              <p>
                <span className={styles.scanItemWeight}>
                  {latestInfoQRCode.weight}
                </span>
                <span className={styles.scanItemUnit}>kg</span>
              </p>
            </div>
          )}
        </div>
        <div className={styles.scannerRight}>
          <div className={styles.scannedListTop}>
            <div className={styles.scannedListTitleContainer}>
              <IconListOutline color="#9f9f9f" />
              <span className={styles.scannedListTitle}>
                {t('qr_scan.scanned_list')}
              </span>
            </div>
            <div>
              <span className={styles.scannedListCount}>
                {totalItemCount}&nbsp;
              </span>
              {t('qr_scan.items')}
            </div>
          </div>
          <div className="scroll-contents">
            <div className={styles.scannedList}>
              {groupedQRCode.length > 0 ? (
                groupedQRCode.map((group, index) => (
                  <div key={index} className={styles.scannedListItemContainer}>
                    <div className="scannedListItemTop">
                      <div className="scannedListItemTitle">
                        <div className="scannedListItemLeftTitle">
                          <span className={styles.dot} />
                          <span className="scannedListItemName">
                            {group.name}
                          </span>
                        </div>
                        <button
                          className="reportAbnormalButton"
                          onClick={() => {
                            const deliveryIdsInGroup = group.data.map(
                              (item) => item.id
                            );

                            setReportingTargetIds(deliveryIdsInGroup);
                          }}
                        >
                          {t('confirm_select_route_item.mark_abnormal')}
                        </button>
                      </div>
                      <div>
                        <span className={styles.scannedListCount}>
                          {calculateTotalWeight(group.data)}&nbsp;
                        </span>
                        <span className="scannedListItemUnit">kg</span>
                      </div>
                    </div>
                    <div className="scannedListItemBottom">
                      {group.data.map((item, index) => {
                        const isChecked = checkedItems.has(item.id);
                        return (
                          <div
                            key={index}
                            className="scannedListItemBottomChildren"
                            onClick={() => handleToggleItem(item.id)}
                            style={{
                              cursor: 'pointer',
                              opacity: isChecked ? 1 : 0.5,
                            }}
                          >
                            <p>
                              <span className="value">
                                {normalizeNumber({ value: item.weight })}&nbsp;
                              </span>
                              <span className="unit">kg</span>
                            </p>
                            {isChecked && (
                              <div className="iconCheck">
                                <IconCheck size={20} />
                              </div>
                            )}
                            {!isChecked && <IconEyeSlash size={25} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noData}>{t('messages.M_008')}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.finishButtonContainer}>
        <button
          className={styles.finishButton}
          disabled={groupedQRCode.length === 0 || checkedItems.size === 0}
          onClick={() => {
            hasEdiEnabledCheckedItem && user.role === RoleType.AdminOperator
              ? setIsShowTruckModal(true)
              : setIsShowConfirmModal(true);
          }}
        >
          <div className={styles.finishButtonContent}>
            <div className={styles.finishButtonIcon}>
              <IconCheck size={20} fillColor="#000" borderColor="#000" />
            </div>
            {t('qr_scan.finish_scanning')}
          </div>
        </button>
      </div>

      {isShowTruckModal && (
        <CollectEmissionsSelectTruckModal
          closeModal={handleCloseTruckModal}
          onSubmit={handleTruckSubmit}
        />
      )}

      {isShowConfirmModal && (
        <ConfirmSelectEmissionItemsModal
          quantity={checkedItems.size}
          totalWeight={calculateCheckedItemsTotalWeight}
          items={getSelectedItems}
          onSelectItems={(isCompleted) => {
            handleCollectEmissionItems(isCompleted);
          }}
          onClose={() => setIsShowConfirmModal(false)}
        />
      )}

      {reportingTargetIds && reportingTargetIds.length > 0 && (
        <Portal>
          <ReportAbnormalityModal
            area={{ name: areaName, id: areaId }}
            targetIds={reportingTargetIds}
            onclose={() => setReportingTargetIds(null)}
          />
        </Portal>
      )}
    </div>
  );
};

export default CollectRouteQRScanning;
